/** A rectangle in container-relative pixels. */
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Where the empty space sits once the video has been fitted:
 * - `horizontal`: bars above and below (video is wider than the container).
 * - `vertical`: bars left and right (video is taller than the container).
 * - `none`: the video fills the container exactly.
 */
export type BarOrientation = 'horizontal' | 'vertical' | 'none';

export interface LetterboxLayout {
  /** Where to draw the video. */
  video: Rect;
  /**
   * The two empty regions flanking the video, in reading order (top then bottom, or left then
   * right). Empty when the video fills the container. The canvas backdrop paints a cropped copy
   * of the feed behind exactly these rectangles.
   */
  bars: Rect[];
  orientation: BarOrientation;
}

const EMPTY: LetterboxLayout = {
  video: { left: 0, top: 0, width: 0, height: 0 },
  bars: [],
  orientation: 'none',
};

/** Sub-pixel differences aren't worth a bar; treat them as a perfect fit. */
const FIT_TOLERANCE_PX = 0.5;

/**
 * Fit a video of the given aspect ratio inside a container, preserving its proportions.
 *
 * @param containerWidth Container width in pixels.
 * @param containerHeight Container height in pixels.
 * @param aspect Video aspect ratio (width / height). Non-finite or non-positive values yield an
 *   empty layout, which is what callers see before the first frame reports its dimensions.
 * @returns The video rectangle plus the empty bars around it.
 */
export function fitBox(
  containerWidth: number,
  containerHeight: number,
  aspect: number,
): LetterboxLayout {
  if (
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(containerHeight) ||
    !Number.isFinite(aspect) ||
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    aspect <= 0
  ) {
    return EMPTY;
  }

  const containerAspect = containerWidth / containerHeight;
  const scaleToWidth = aspect > containerAspect;

  const width = scaleToWidth ? containerWidth : containerHeight * aspect;
  const height = scaleToWidth ? containerWidth / aspect : containerHeight;
  const left = (containerWidth - width) / 2;
  const top = (containerHeight - height) / 2;
  const video = { left, top, width, height };

  if (scaleToWidth) {
    if (top <= FIT_TOLERANCE_PX) return { video, bars: [], orientation: 'none' };
    return {
      video,
      bars: [
        { left: 0, top: 0, width: containerWidth, height: top },
        { left: 0, top: top + height, width: containerWidth, height: top },
      ],
      orientation: 'horizontal',
    };
  }

  if (left <= FIT_TOLERANCE_PX) return { video, bars: [], orientation: 'none' };
  return {
    video,
    bars: [
      { left: 0, top: 0, width: left, height: containerHeight },
      { left: left + width, top: 0, width: left, height: containerHeight },
    ],
    orientation: 'vertical',
  };
}
